import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AvailabilityDisplay } from '../components/cryptic/AvailabilityDisplay';
import { CommandBar } from '../components/cryptic/CommandBar';
import { PricingDisplay } from '../components/cryptic/PricingDisplay';
import { SellDisplay } from '../components/cryptic/SellDisplay';
import { TripSummary } from '../components/cryptic/TripSummary';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { Availability, CrypticNotice, CrypticResponse, Pricing, Segment, SeatLine, SoldSegment, TicketLine, Traveler } from '../lib/types';

/** The six lettered work areas the host has always offered. */
const WORK_AREAS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
type WorkAreaLetter = (typeof WORK_AREAS)[number];

/** What a screen block holds, before it is given its position on the screen. */
type BlockContent =
  | { kind: 'echo'; entry: string }
  | { kind: 'text' | 'error'; lines: string[] }
  | { kind: 'notice'; notice: CrypticNotice }
  | { kind: 'availability'; availability: Availability }
  | { kind: 'sell'; sold: SoldSegment[] }
  | { kind: 'pricing'; pricing: Pricing }
  | { kind: 'success'; lines: string[]; notice: CrypticNotice };

type Block = BlockContent & { id: number };

interface WorkAreaState {
  blocks: Block[];
  history: string[];
  itinerary: Segment[];
  travelers: Traveler[];
  /** Empty until ER files the PNR. */
  recordLocator: string;
  tickets: TicketLine[];
  seats: SeatLine[];
}

const emptyArea = (): WorkAreaState => ({
  blocks: [],
  history: [],
  itinerary: [],
  travelers: [],
  recordLocator: '',
  tickets: [],
  seats: [],
});

function initialAreas(): Record<WorkAreaLetter, WorkAreaState> {
  return Object.fromEntries(WORK_AREAS.map((letter) => [letter, emptyArea()])) as Record<
    WorkAreaLetter,
    WorkAreaState
  >;
}

/**
 * The cryptic workspace: the host command line an agent actually works in.
 * Each lettered tab is an independent work area with its own screen and its own
 * host state, so a search in A survives while B is used for something else.
 */
export function CrypticPage() {
  const { user, pcc, signOut } = useAuth();
  const navigate = useNavigate();

  const [areas, setAreas] = useState<Record<WorkAreaLetter, WorkAreaState>>(initialAreas);
  const [active, setActive] = useState<WorkAreaLetter>('A');
  const [busy, setBusy] = useState(false);
  const [tripCollapsed, setTripCollapsed] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const nextBlockId = useRef(1);
  const greeted = useRef(new Set<WorkAreaLetter>());
  const screenRef = useRef<HTMLDivElement>(null);

  const area = areas[active];
  const canManageUsers = user?.role === 'root' || user?.role === 'admin';

  const appendBlocks = useCallback((letter: WorkAreaLetter, blocks: BlockContent[]) => {
    setAreas((prev) => ({
      ...prev,
      [letter]: {
        ...prev[letter],
        blocks: [...prev[letter].blocks, ...blocks.map((block) => ({ ...block, id: nextBlockId.current++ }))],
      },
    }));
  }, []);

  // Opening a work area prints the host sign-on banner, once. The guard is a
  // ref rather than state so it survives the double effect pass StrictMode
  // makes in development, which would otherwise print the banner twice.
  useEffect(() => {
    if (greeted.current.has(active)) return;
    greeted.current.add(active);

    api
      .crypticGreeting(active)
      .then((greeting) => appendBlocks(active, [{ kind: 'text', lines: greeting.lines }]))
      .catch((error: unknown) => {
        const message = error instanceof ApiError ? error.message : 'Cannot reach the host.';
        appendBlocks(active, [{ kind: 'error', lines: [message.toUpperCase()] }]);
      });
  }, [active, appendBlocks]);

  // A response always lands at the bottom of the screen, where the eye is, and
  // back at the left margin — a wide display can leave the screen scrolled
  // sideways, which would hide the line numbers on whatever comes next.
  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) return;
    screen.scrollTop = screen.scrollHeight;
    screen.scrollLeft = 0;
  }, [area.blocks]);

  const send = useCallback(
    async (entry: string) => {
      const letter = active;
      setHistoryOpen(false);
      setBusy(true);

      setAreas((prev) => ({
        ...prev,
        [letter]: {
          ...prev[letter],
          history: [...prev[letter].history.filter((h) => h !== entry), entry].slice(-50),
          blocks: [...prev[letter].blocks, { id: nextBlockId.current++, kind: 'echo', entry }],
        },
      }));

      try {
        const response: CrypticResponse = await api.sendCrypticEntry(entry, letter);

        if (response.kind === 'clear') {
          // Ignore wipes the work area, then prints what the host prints.
          setAreas((prev) => ({
            ...prev,
            [letter]: {
              ...prev[letter],
              itinerary: [],
              travelers: [],
              recordLocator: '',
              tickets: [],
              seats: [],
              blocks: [{ id: nextBlockId.current++, kind: 'text', lines: response.lines ?? ['IGND'] }],
            },
          }));
          return;
        }

        // A reply that touched the itinerary carries the whole of it back.
        if (response.itinerary) {
          const itinerary = response.itinerary;
          setAreas((prev) => ({ ...prev, [letter]: { ...prev[letter], itinerary } }));
        }
        if (response.recordLocator !== undefined) {
          const recordLocator = response.recordLocator;
          setAreas((prev) => ({ ...prev, [letter]: { ...prev[letter], recordLocator } }));
        }
        if (response.tickets) {
          const tickets = response.tickets;
          setAreas((prev) => ({ ...prev, [letter]: { ...prev[letter], tickets } }));
        }
        if (response.seats) {
          const seats = response.seats;
          setAreas((prev) => ({ ...prev, [letter]: { ...prev[letter], seats } }));
        }
        if (response.travelers) {
          const travelers = response.travelers;
          setAreas((prev) => ({ ...prev, [letter]: { ...prev[letter], travelers } }));
        }

        if (response.kind === 'availability' && response.availability) {
          appendBlocks(letter, [{ kind: 'availability', availability: response.availability }]);
        } else if (response.kind === 'sell' && response.sold?.length) {
          appendBlocks(letter, [{ kind: 'sell', sold: response.sold }]);
        } else if (response.kind === 'success') {
          appendBlocks(letter, [
            {
              kind: 'success',
              lines: (response.lines ?? []).filter((line) => line.trim() !== ''),
              notice: response.notice ?? { title: 'SUCCESS', detail: '' },
            },
          ]);
        } else if (response.kind === 'pricing' && response.pricing) {
          appendBlocks(letter, [{ kind: 'pricing', pricing: response.pricing }]);
        } else if (response.kind === 'notice' && response.notice) {
          appendBlocks(letter, [{ kind: 'notice', notice: response.notice }]);
        } else {
          // A reply with nothing to print prints nothing. An empty block is not
          // invisible — it still draws its rule, which reads as a second line
          // under whatever came before it.
          const lines = (response.lines ?? []).filter((line) => line.trim() !== '');
          if (lines.length > 0) {
            appendBlocks(letter, [{ kind: response.kind === 'error' ? 'error' : 'text', lines }]);
          } else if (response.kind === 'error') {
            // An error that says nothing is still an error: silence would leave
            // the agent thinking the entry worked.
            appendBlocks(letter, [{ kind: 'error', lines: ['UNABLE TO PROCESS'] }]);
          }
        }
      } catch (error: unknown) {
        const message = error instanceof ApiError ? error.message : 'Cannot reach the host.';
        appendBlocks(letter, [{ kind: 'error', lines: [message.toUpperCase()] }]);
      } finally {
        setBusy(false);
      }
    },
    [active, appendBlocks],
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true, state: { loggedOut: true } });
  };

  const historyEntries = useMemo(() => [...area.history].reverse(), [area.history]);

  return (
    <div className="cryptic">
      <div className="cryptic__tabs" role="tablist" aria-label="Work areas">
        {WORK_AREAS.map((letter) => (
          <button
            key={letter}
            type="button"
            role="tab"
            aria-selected={letter === active}
            className={`cryptic__tab${letter === active ? ' cryptic__tab--active' : ''}`}
            onClick={() => setActive(letter)}
          >
            <span className="cryptic__tab-letter">{letter}</span>
            {(letter === active || greeted.current.has(letter)) && (
              <span className="cryptic__tab-pcc">{tabLabel(areas[letter], pcc)}</span>
            )}
          </button>
        ))}
      </div>

      <CommandBar
        pcc={pcc}
        busy={busy}
        history={area.history}
        onSubmit={send}
        onShowHistory={() => setHistoryOpen((open) => !open)}
        onShowHelp={() => send('HELP')}
      />

      <div className="pfkeys">
        <span className="pfkeys__empty">No PF Keys defined</span>
        <button type="button" className="pfkeys__menu" title="Not implemented" disabled aria-label="PF key options">
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <circle cx="12" cy="5.5" r="1.7" fill="currentColor" />
            <circle cx="12" cy="12" r="1.7" fill="currentColor" />
            <circle cx="12" cy="18.5" r="1.7" fill="currentColor" />
          </svg>
        </button>
        <button type="button" className="pfkeys__trip" onClick={() => setTripCollapsed((collapsed) => !collapsed)}>
          <span className="pfkeys__dot" aria-hidden="true" />
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M3.5 8.5h17v10h-17zM9 8.5V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
          Trip Summary
        </button>
      </div>

      {historyOpen && (
        <div className="cryptic__history">
          <div className="cryptic__history-title">Entries sent in work area {active}</div>
          {historyEntries.length === 0 ? (
            <p className="cryptic__history-empty">Nothing sent yet.</p>
          ) : (
            <ul>
              {historyEntries.map((entry) => (
                <li key={entry}>
                  <button type="button" onClick={() => send(entry)}>
                    {entry}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="cryptic__body">
        <div className="cryptic__screen" ref={screenRef} role="log" aria-live="polite" aria-label="Host responses">
          {area.blocks.map((block) => (
            <ScreenBlock key={block.id} block={block} onSend={send} />
          ))}
          {busy && <div className="cryptic__working">WORKING…</div>}
        </div>

        {!tripCollapsed && (
          <TripSummary
            travelers={area.travelers}
            itinerary={area.itinerary}
            tickets={area.tickets}
            seats={area.seats}
            recordLocator={area.recordLocator}
            onCommand={send}
            busy={busy}
            onToggleCollapsed={() => setTripCollapsed((collapsed) => !collapsed)}
          />
        )}

        <nav className="rail" aria-label="Workspace">
          <button
            type="button"
            className="rail__item rail__item--active"
            onClick={() => setTripCollapsed(false)}
          >
            <RailIcon path="M3 5h12v9H3zM3 8h12" />
            Trip Summary
          </button>
          <button type="button" className="rail__item" disabled title="Not implemented">
            <RailIcon path="M9 4v10M4 9h10" />
            Profiles
          </button>
          <button type="button" className="rail__item" disabled title="Not implemented">
            <RailIcon path="M4 9a5 5 0 0 1 10 0 5 5 0 0 1-10 0Z" />
            Workflows
          </button>
          <button type="button" className="rail__item" disabled title="Not implemented">
            <RailIcon path="M3 3h5v5H3zm7 0h5v5h-5zM3 10h5v5H3zm7 0h5v5h-5z" />
            Applications
          </button>

          <div className="rail__spacer" />

          {canManageUsers && (
            <Link className="rail__item" to="/admin/users">
              <RailIcon path="M9 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3 16a6 6 0 0 1 12 0" />
              Agency Admin
            </Link>
          )}
          <Link className="rail__item" to="/overview">
            <RailIcon path="M9 6.5A2.5 2.5 0 1 0 9 11.5 2.5 2.5 0 0 0 9 6.5ZM9 2v2m0 10v2m7-7h-2M4 9H2" />
            Settings
          </Link>
          <button type="button" className="rail__item" onClick={handleSignOut}>
            <RailIcon path="M11 5V3H3v12h8v-2M8 9h8m0 0-3-3m3 3-3 3" />
            Sign out
          </button>
        </nav>
      </div>
    </div>
  );
}

/** Initials of the given names, title left out: MUHAMMAD SAIFUL MR is MS. */
const TITLES = new Set(['MR', 'MRS', 'MS', 'MISS', 'MSTR', 'DR', 'PROF']);
function initials(first: string): string {
  return first
    .split(/\s+/)
    .filter((word) => word && !TITLES.has(word))
    .map((word) => word[0])
    .join('');
}

/**
 * What a work-area tab reads: the filed PNR and its lead passenger once one is
 * loaded (OVXANP - FAISAL SM), and the agency PCC otherwise.
 */
function tabLabel(area: WorkAreaState, pcc: string): string {
  if (!area.recordLocator) return pcc || '----';
  const lead = area.travelers.find((t) => !t.infant) ?? area.travelers[0];
  if (!lead) return area.recordLocator;
  return `${area.recordLocator} - ${lead.last} ${initials(lead.first)}`.trim();
}

function RailIcon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function ScreenBlock({ block, onSend }: { block: Block; onSend: (entry: string) => void }) {
  switch (block.kind) {
    case 'echo':
      return (
        <div className="screen__echo">
          {block.entry}
          <span className="screen__enter">«</span>
        </div>
      );

    case 'availability':
      return <AvailabilityDisplay availability={block.availability} onSell={onSend} />;

    case 'success':
      return (
        <>
          {block.lines.length > 0 && <ScreenText lines={block.lines} />}
          <div className={`screen__success${block.notice.detail ? ' screen__success--detail' : ''}`} role="status">
            <svg viewBox="0 0 40 40" width="38" height="38" aria-hidden="true">
              <circle cx="20" cy="20" r="19" fill="#7cb342" />
              <path
                d="M11 20.5l6 6 12-13"
                fill="none"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div>
              <div className="screen__success-title">{block.notice.title}</div>
              {block.notice.detail && <div className="screen__success-detail">{block.notice.detail}</div>}
            </div>
          </div>
        </>
      );

    case 'pricing':
      return <PricingDisplay pricing={block.pricing} onSavePrice={() => onSend('WPRQ')} />;

    case 'sell':
      return <SellDisplay sold={block.sold} onReturnAvailability={() => onSend('1*')} />;

    case 'notice':
      return (
        <div className="screen__notice" role="status">
          <span className="screen__notice-icon" aria-hidden="true">
            i
          </span>
          <div>
            <div className="screen__notice-title">{block.notice.title}</div>
            <div className="screen__notice-detail">{block.notice.detail}</div>
          </div>
          {block.notice.action && (
            <button type="button" className="screen__notice-action" disabled>
              {block.notice.action}
            </button>
          )}
        </div>
      );

    default:
      return (
        <ScreenText lines={block.lines} error={block.kind === 'error'} />
      );
  }
}

/** A host response, one row per line as the host lays it out: each line is its
 *  own padded row, which is what sets the pitch down the screen. */
function ScreenText({ lines, error }: { lines: string[]; error?: boolean }) {
  return (
    <pre className={`screen__text${error ? ' screen__text--error' : ''}`}>
      {lines.map((line, i) => (
        <div key={i} className="screen__line">
          {line || ' '}
        </div>
      ))}
    </pre>
  );
}
